
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HearingData {
  name: string;
  email: string;
  company: string;
  service: string;
  projectScale: string;
  startTiming: string;
  budget: string;
  message: string;
}

export default function ContactSection() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [hearingData, setHearingData] = useState<HearingData>({
    name: '',
    email: '',
    company: '',
    service: '',
    projectScale: '',
    startTiming: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const totalSteps = 6;

  const handleBasicInfoNext = () => {
    if (hearingData.name && hearingData.email) {
      setCurrentStep(2);
    }
  };

  const handleServiceSelect = (service: string) => {
    setHearingData({ ...hearingData, service });
    setCurrentStep(3);
  };

  const handleProjectScaleSelect = (scale: string) => {
    setHearingData({ ...hearingData, projectScale: scale });
    setCurrentStep(4);
  };

  const handleStartTimingSelect = (timing: string) => {
    setHearingData({ ...hearingData, startTiming: timing });
    setCurrentStep(5);
  };

  const handleBudgetSelect = (budget: string) => {
    setHearingData({ ...hearingData, budget });
    setCurrentStep(6);
  };

  const handleMessageNext = () => {
    setCurrentStep(7);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new URLSearchParams();
      formData.append('name', hearingData.name);
      formData.append('email', hearingData.email);
      formData.append('company', hearingData.company);
      formData.append('service', hearingData.service);
      formData.append('projectScale', hearingData.projectScale);
      formData.append('startTiming', hearingData.startTiming);
      formData.append('budget', hearingData.budget);
      formData.append('message', hearingData.message);

      const response = await fetch('https://readdy.ai/api/form/d3s6qb41cr8rcdqfdllg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setHearingData({
      name: '',
      email: '',
      company: '',
      service: '',
      projectScale: '',
      startTiming: '',
      budget: '',
      message: '',
    });
    setCurrentStep(1);
    setSubmitStatus(null);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-stone-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-thin text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold text-stone-800 mb-4">
              {t('contact.contactInfo')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-stone-700"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-2">{t('contactAddress')}</h3>
                  <p
                    className="text-stone-600 text-sm"
                    dangerouslySetInnerHTML={{
                      __html: t('contactAddressValue'),
                    }}
                  ></p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-800 mb-1">{t('contact.phone')}</h4>
                  <p className="text-stone-600 text-sm">{t('contact.phoneNumber')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-800 mb-1">{t('contact.emailLabel')}</h4>
                  <p className="text-stone-600 text-sm">info@h-nichinan.co.jp</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-800 mb-1">{t('contact.businessHours')}</h4>
                  <p className="text-stone-600 text-sm">
                    {t('contact.businessHoursDetail')}
                    <br />
                    {t('contact.businessHoursClosed')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-stone-50 rounded-xl p-6">
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-2">{t('contact.submitSuccess')}</h3>
                <p className="text-stone-600 mb-6">{t('contact.submitSuccessMessage')}</p>
                <button
                  onClick={handleReset}
                  className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-700 text-white hover:bg-stone-800 px-6 py-2 text-sm rounded-md"
                >
                  {t('contact.newInquiry')}
                </button>
              </div>
            ) : (
              <form id="contact-hearing-form" data-readdy-form="true">
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-stone-700">
                      {t('contact.step')} {Math.min(currentStep, totalSteps)} / {totalSteps}
                    </span>
                    <span className="text-sm text-stone-500">
                      {Math.round((Math.min(currentStep, totalSteps) / totalSteps) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-stone-200 rounded-full h-2">
                    <div
                      className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(Math.min(currentStep, totalSteps) / totalSteps) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.basicInfo')}</h3>
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-stone-700 mb-1"
                      >
                        {t('contact.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={hearingData.name}
                        onChange={(e) =>
                          setHearingData({ ...hearingData, name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-5
                            00 focus:border-transparent text-sm"
                        placeholder={t('contact.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-stone-700 mb-1"
                      >
                        {t('contact.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={hearingData.email}
                        onChange={(e) =>
                          setHearingData({ ...hearingData, email: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        placeholder={t('contact.emailPlaceholder')}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-stone-700 mb-1"
                      >
                        {t('contact.company')}
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={hearingData.company}
                        onChange={(e) =>
                          setHearingData({ ...hearingData, company: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        placeholder={t('contact.companyPlaceholder')}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleBasicInfoNext}
                      disabled={!hearingData.name || !hearingData.email}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-700 text-white hover:bg-stone-800 disabled:bg-stone-300 disabled:cursor-not-allowed px-6 py-2 text-sm rounded-md w-full"
                    >
                      {t('contact.next')} <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                )}

                {/* Step 2: Service Selection */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">
                      {t('contact.serviceInterest')}
                    </h3>
                    <p className="text-sm text-stone-600 mb-4">{t('contact.serviceInterestDesc')}</p>
                    <div className="space-y-2">
                      {[t('contact.service1'), t('contact.service2'), t('contact.service3'), t('contact.service4'), t('contact.service5')].map(
                        (service) => (
                          <button
                            key={service}
                            type="button"
                            onClick={() => handleServiceSelect(service)}
                            className="whitespace-nowrap cursor-pointer w-full text-left px-4 py-3 border-2 border-stone-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all duration-200 text-sm font-medium text-stone-700"
                          >
                            {service}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-200 text-stone-700 hover:bg-stone-300 px-6 py-2 text-sm rounded-md w-full mt-4"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      {t('contact.back')}
                    </button>
                  </div>
                )}

                {/* Step 3: Project Scale */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.projectScale')}</h3>
                    <p className="text-sm text-stone-600 mb-4">{t('contact.projectScaleDesc')}</p>
                    <div className="space-y-2">
                      {[t('contact.scale1'), t('contact.scale2'), t('contact.scale3'), t('contact.scale4')].map(
                        (scale) => (
                          <button
                            key={scale}
                            type="button"
                            onClick={() => handleProjectScaleSelect(scale)}
                            className="whitespace-nowrap cursor-pointer w-full text-left px-4 py-3 border-2 border-stone-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all duration-200 text-sm font-medium text-stone-700"
                          >
                            {scale}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-200 text-stone-700 hover:bg-stone-300 px-6 py-2 text-sm rounded-md w-full mt-4"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      {t('contact.back')}
                    </button>
                  </div>
                )}

                {/* Step 4: Start Timing */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.startTiming')}</h3>
                    <p className="text-sm text-stone-600 mb-4">{t('contact.startTimingDesc')}</p>
                    <div className="space-y-2">
                      {[t('contact.timing1'), t('contact.timing2'), t('contact.timing3'), t('contact.timing4'), t('contact.timing5')].map(
                        (timing) => (
                          <button
                            key={timing}
                            type="button"
                            onClick={() => handleStartTimingSelect(timing)}
                            className="whitespace-nowrap cursor-pointer w-full text-left px-4 py-3 border-2 border-stone-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all 
                            duration-200 text-sm font-medium text-stone-700"
                          >
                            {timing}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-200 text-stone-700 hover:bg-stone-300 px-6 py-2 text-sm rounded-md w-full mt-4"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      {t('contact.back')}
                    </button>
                  </div>
                )}

                {/* Step 5: Budget */}
                {currentStep === 5 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.budget')}</h3>
                    <p className="text-sm text-stone-600 mb-4">{t('contact.budgetDesc')}</p>
                    <div className="space-y-2">
                      {[t('contact.budget1'), t('contact.budget2'), t('contact.budget3'), t('contact.budget4'), t('contact.budget5')].map(
                        (budget) => (
                          <button
                            key={budget}
                            type="button"
                            onClick={() => handleBudgetSelect(budget)}
                            className="whitespace-nowrap cursor-pointer w-full text-left px-4 py-3 border-2 border-stone-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-all 
                            duration-200 text-sm font-medium text-stone-700"
                          >
                            {budget}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 
                      bg-stone-200 text-stone-700 hover:bg-stone-300 px-6 py-2 text-sm rounded-md w-full mt-4"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      {t('contact.back')}
                    </button>
                  </div>
                )}

                {/* Step 6: Additional Message */}
                {currentStep === 6 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.detailMessage')}</h3>
                    <p className="text-sm text-stone-600 mb-4">{t('contact.detailMessageDesc')}</p>
                    <div>
                      <textarea
                        id="message"
                        name="message"
                        value={hearingData.message}
                        onChange={(e) =>
                          setHearingData({ ...hearingData, message: e.target.value })
                        }
                        maxLength={500}
                        rows={6}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                        placeholder={t('contact.messagePlaceholder')}
                      ></textarea>
                      <div className="text-right text-xs text-stone-500 mt-1">
                        {hearingData.message.length}/500{t('contact.characters')}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-200 text-stone-700 hover:bg-stone-300 px-6 py-2 text-sm rounded-md flex-1"
                      >
                        <i className="ri-arrow-left-line mr-2"></i>
                        {t('contact.back')}
                      </button>
                      <button
                        type="button"
                        onClick={handleMessageNext}
                        className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-700 text-white hover:bg-stone-800 px-6 py-2 text-sm rounded-md flex-1"
                      >
                        {t('contact.toConfirm')} <i className="ri-arrow-right-line ml-2"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 7: Summary */}
                {currentStep === 7 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">{t('contact.confirmInput')}</h3>
                    <div className="bg-white rounded-lg p-4 space-y-3 border border-stone-200">
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.name')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.name}</p>
                      </div>
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.email')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.email}</p>
                      </div>
                      {hearingData.company && (
                        <div className="border-b border-stone-200 pb-2">
                          <p className="text-xs text-stone-500 mb-1">{t('contact.company')}</p>
                          <p className="text-sm font-medium text-stone-800">{hearingData.company}</p>
                        </div>
                      )}
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.serviceInterest')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.service}</p>
                      </div>
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.projectScale')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.projectScale}</p>
                      </div>
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.startTiming')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.startTiming}</p>
                      </div>
                      <div className="border-b border-stone-200 pb-2">
                        <p className="text-xs text-stone-500 mb-1">{t('contact.budget')}</p>
                        <p className="text-sm font-medium text-stone-800">{hearingData.budget}</p>
                      </div>
                      {hearingData.message && (
                        <div>
                          <p className="text-xs text-stone-500 mb-1">{t('contact.detailMessage')}</p>
                          <p className="text-sm font-medium text-stone-800 whitespace-pre-wrap">
                            {hearingData.message}
                          </p>
                        </div>
                      )}
                    </div>

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                        <i className="ri-error-warning-line text-red-500 text-lg mr-2 mt-0.5"></i>
                        <div>
                          <p className="text-sm font-medium text-red-800">{t('contact.submitError')}</p>
                          <p className="text-xs text-red-600 mt-1">{t('contact.submitErrorMessage')}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={isSubmitting}
                        className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-stone-200 text-stone-700 hover:bg-stone-300 disabled:bg-stone-100 disabled:cursor-not-allowed px-6 py-2 text-sm rounded-md flex-1"
                      >
                        <i className="ri-arrow-left-line mr-2"></i>
                        {t('contact.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 bg-amber-500 text-white hover:bg-amber-600 disabled:bg-stone-300 disabled:cursor-not-allowed px-6 py-2 text-sm rounded-md flex-1 flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            {t('contact.sending')}
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line mr-2"></i>
                            {t('contact.send')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
